import { useState, useMemo, useCallback } from 'react';

export interface PipeStatusRow {
  slNo: number;
  pipeID: string;
  projectNumber: string;
  sequence: string;
  status: string;
  endA: boolean;
  endB: boolean;
  isArchive: boolean;
  isFlange: boolean;
  isAnode: boolean;
  groupId?: string;
}

export type EndPresenceOption = 'both' | 'endAOnly' | 'endBOnly';
/** @deprecated Use EndPresenceOption[]; empty array means all ends */
export type EndPresenceFilter = 'all' | EndPresenceOption;
export type FlagFilter = 'all' | 'yes' | 'no';
/** Flange / anode / normal — single pipe-mark filter in the toolbar. */
export type PipeMarkFilter = 'all' | 'flange' | 'anode' | 'normal';

export interface PipeStatusFiltersState {
  pipeID: string[];
  groupId: string[];
  sequence: string[];
  status: string[];
  endA: string[];
  endB: string[];
  /** Selected end-presence options; empty = all ends */
  endPresence: EndPresenceOption[];
  archive: FlagFilter;
  pipeMark: PipeMarkFilter;
  /**
   * Within each group: same Pipe ID appears more than once.
   * yes = only those duplicate rows; no = only unique Pipe IDs per group; all = no filter.
   */
  duplicates: FlagFilter;
}

export interface PipeEndPresenceCounts {
  both: number;
  endAOnly: number;
  endBOnly: number;
}

export interface PipeFlagCounts {
  archive: number;
  flange: number;
  anode: number;
}

export function countPipeEndPresence(rows: Pick<PipeStatusRow, 'endA' | 'endB'>[]): PipeEndPresenceCounts {
  let both = 0;
  let endAOnly = 0;
  let endBOnly = 0;

  for (const row of rows) {
    if (row.endA && row.endB) both += 1;
    else if (row.endA) endAOnly += 1;
    else if (row.endB) endBOnly += 1;
  }

  return { both, endAOnly, endBOnly };
}

export function countPipeFlags(
  rows: Pick<PipeStatusRow, 'isArchive' | 'isFlange' | 'isAnode'>[]
): PipeFlagCounts {
  let archive = 0;
  let flange = 0;
  let anode = 0;
  for (const row of rows) {
    if (row.isArchive) archive += 1;
    if (row.isFlange) flange += 1;
    if (row.isAnode) anode += 1;
  }
  return { archive, flange, anode };
}

function rowMatchesEndPresenceFilter(row: PipeStatusRow, endPresence: EndPresenceOption[]): boolean {
  if (!endPresence || endPresence.length === 0) return true;
  return endPresence.some((opt) => {
    if (opt === 'both') return row.endA && row.endB;
    if (opt === 'endAOnly') return row.endA && !row.endB;
    if (opt === 'endBOnly') return !row.endA && row.endB;
    return false;
  });
}

function rowMatchesFlagFilter(flagOn: boolean, filter: FlagFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'yes') return flagOn;
  return !flagOn;
}

function rowMatchesPipeMarkFilter(
  row: Pick<PipeStatusRow, 'isFlange' | 'isAnode'>,
  filter: PipeMarkFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'flange') return row.isFlange;
  if (filter === 'anode') return row.isAnode;
  return !row.isFlange && !row.isAnode;
}

interface UsePipeStatusFiltersProps {
  /** Full project pipe list when searching; otherwise the current server page. */
  data: PipeStatusRow[];
}

const EMPTY_FILTERS: PipeStatusFiltersState = {
  pipeID: [],
  groupId: [],
  sequence: [],
  status: [],
  endA: [],
  endB: [],
  endPresence: [],
  archive: 'all',
  pipeMark: 'all',
  duplicates: 'all',
};

export function formatPipeStatusGroupLabel(groupId: string | undefined | null): string {
  const id = String(groupId ?? '').trim();
  return id === '' ? 'Ungrouped' : id;
}

/** Key for duplicate detection: same Pipe ID within the same group. */
export function pipeGroupDuplicateKey(row: Pick<PipeStatusRow, 'groupId' | 'pipeID'>): string {
  return `${formatPipeStatusGroupLabel(row.groupId)}::${String(row.pipeID ?? '').trim()}`;
}

/** Keys (group + pipe ID) that appear more than once in the dataset. */
export function buildDuplicatePipeKeysInGroup(
  data: Pick<PipeStatusRow, 'groupId' | 'pipeID'>[]
): Set<string> {
  const counts = new Map<string, number>();
  for (const row of data) {
    const key = pipeGroupDuplicateKey(row);
    if (!String(row.pipeID ?? '').trim()) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [key, count] of Array.from(counts.entries())) {
    if (count > 1) duplicates.add(key);
  }
  return duplicates;
}

/** Group duplicates together: same group, then same Pipe ID, then original order. */
export function sortDuplicatePipeRowsTogether(rows: PipeStatusRow[]): PipeStatusRow[] {
  return [...rows]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const groupA = formatPipeStatusGroupLabel(a.row.groupId);
      const groupB = formatPipeStatusGroupLabel(b.row.groupId);
      const groupCmp = groupA.localeCompare(groupB, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      if (groupCmp !== 0) return groupCmp;

      const pipeCmp = String(a.row.pipeID ?? '').localeCompare(String(b.row.pipeID ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      if (pipeCmp !== 0) return pipeCmp;

      return a.index - b.index;
    })
    .map(({ row }) => row);
}

export function filterPipeStatusRows(
  data: PipeStatusRow[],
  searchQuery: string,
  filters: PipeStatusFiltersState
): PipeStatusRow[] {
  const searchLower = searchQuery.trim().toLowerCase();
  const duplicatesMode = filters.duplicates ?? 'all';
  const duplicateKeys =
    duplicatesMode === 'all' ? null : buildDuplicatePipeKeysInGroup(data);

  const filtered = data.filter((row) => {
    const groupLabel = formatPipeStatusGroupLabel(row.groupId);
    const matchesSearch =
      !searchLower ||
      [
        row.slNo,
        row.pipeID,
        groupLabel,
        row.projectNumber,
        row.sequence,
        row.status,
        String(row.endA),
        String(row.endB),
      ].some((val) => String(val ?? '').toLowerCase().includes(searchLower));

    const statusDisplay = row.status === 'defaultState' ? 'Missing One End' : row.status;
    const matchesPipeID = filters.pipeID.length === 0 || filters.pipeID.includes(row.pipeID);
    const matchesGroupId =
      filters.groupId.length === 0 || filters.groupId.includes(groupLabel);
    const matchesSequence = filters.sequence.length === 0 || filters.sequence.includes(row.sequence);
    const matchesStatus = filters.status.length === 0 || filters.status.includes(statusDisplay);
    const matchesEndA = filters.endA.length === 0 || filters.endA.includes(String(row.endA));
    const matchesEndB = filters.endB.length === 0 || filters.endB.includes(String(row.endB));
    const matchesEndPresence = rowMatchesEndPresenceFilter(row, filters.endPresence);
    const matchesArchive = rowMatchesFlagFilter(row.isArchive, filters.archive ?? 'all');
    const matchesPipeMark = rowMatchesPipeMarkFilter(row, filters.pipeMark ?? 'all');
    const isDup = duplicateKeys?.has(pipeGroupDuplicateKey(row)) ?? false;
    const matchesDuplicates = rowMatchesFlagFilter(isDup, duplicatesMode);

    return (
      matchesSearch &&
      matchesPipeID &&
      matchesGroupId &&
      matchesSequence &&
      matchesStatus &&
      matchesEndA &&
      matchesEndB &&
      matchesEndPresence &&
      matchesArchive &&
      matchesPipeMark &&
      matchesDuplicates
    );
  });

  // Keep matching Pipe IDs (same group) consecutive when reviewing duplicates.
  if (duplicatesMode === 'yes') {
    return sortDuplicatePipeRowsTogether(filtered);
  }

  return filtered;
}

export const usePipeStatusFilters = ({ data }: UsePipeStatusFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<PipeStatusFiltersState>(EMPTY_FILTERS);

  const uniquePipeIDs = useMemo(() => {
    const set = new Set<string>();
    data.forEach((row) => {
      if (row.pipeID) set.add(row.pipeID);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const uniqueGroupIds = useMemo(() => {
    const set = new Set<string>();
    data.forEach((row) => {
      set.add(formatPipeStatusGroupLabel(row.groupId));
    });
    return Array.from(set).sort((a, b) => {
      if (a === 'Ungrouped') return 1;
      if (b === 'Ungrouped') return -1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [data]);

  const uniqueSequences = useMemo(() => {
    const set = new Set<string>();
    data.forEach((row) => {
      if (row.sequence) set.add(row.sequence);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const uniqueStatuses = useMemo(() => {
    const set = new Set<string>();
    data.forEach((row) => {
      if (row.status) set.add(row.status === 'defaultState' ? 'Missing One End' : row.status);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const endPresenceCounts = useMemo(() => countPipeEndPresence(data), [data]);
  const flagCounts = useMemo(() => countPipeFlags(data), [data]);

  const filteredData = useMemo(
    () => filterPipeStatusRows(data, searchQuery, filters),
    [data, searchQuery, filters]
  );

  const hasActiveFilters = useMemo(
    () =>
      searchQuery.trim() !== '' ||
      filters.pipeID.length > 0 ||
      filters.groupId.length > 0 ||
      filters.sequence.length > 0 ||
      filters.status.length > 0 ||
      filters.endA.length > 0 ||
      filters.endB.length > 0 ||
      filters.endPresence.length > 0 ||
      filters.archive !== 'all' ||
      filters.pipeMark !== 'all' ||
      filters.duplicates !== 'all',
    [searchQuery, filters]
  );

  const onFilterChange = useCallback((key: keyof PipeStatusFiltersState) => (value: string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onEndPresenceChange = useCallback((value: EndPresenceOption[]) => {
    setFilters((prev) => ({ ...prev, endPresence: value }));
  }, []);

  const onArchiveChange = useCallback((value: FlagFilter) => {
    setFilters((prev) => ({ ...prev, archive: value }));
  }, []);

  const onPipeMarkChange = useCallback((value: PipeMarkFilter) => {
    setFilters((prev) => ({ ...prev, pipeMark: value }));
  }, []);

  const onDuplicatesChange = useCallback((value: FlagFilter) => {
    setFilters((prev) => ({ ...prev, duplicates: value }));
  }, []);

  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({ ...EMPTY_FILTERS });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    onEndPresenceChange,
    onArchiveChange,
    onPipeMarkChange,
    onDuplicatesChange,
    uniquePipeIDs,
    uniqueGroupIds,
    uniqueSequences,
    uniqueStatuses,
    filteredData,
    hasActiveFilters,
    filteredCount: filteredData.length,
    endPresenceCounts,
    flagCounts,
    onResetFilters,
  };
};
